'use client';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import Link from 'next/link';
import { apiClientWithoutAuth } from '../utils/apiClient';
import imageBase from '../utils/imageBase';

// --- Types ---
interface AccommodationMedia {
  thumbnailURL: string;
}

interface AccommodationData {
  id: number;
  name: string;
  enName: string;
  cityName: string;
  FaCityName: string;
  starRating: number;
  media: AccommodationMedia[];
}

interface ApiResponse {
  value: {
    isSuccess: boolean;
    data: {
      items: AccommodationData[];
    };
  };
}

// --- Constants ---
const API_ENDPOINT = '/Home/top-visited-accommodations';
const DISPLAY_LIMIT = 3; // نمایش دقیق ۳ آیتم

// --- Helper Functions ---
const createSlug = (text: string) => text.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');

// --- Components ---

// 1. Loading Spinner (Isolated)
const LoadingSpinner = () => (
  <div className="ir-loading">
    <div className="ir-spinner"></div>
    <style jsx>{`
      .ir-loading { display: flex; justify-content: center; padding: 50px; }
      .ir-spinner {
        width: 30px; height: 30px;
        border: 3px solid rgba(255,255,255,0.3);
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 1s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
    `}</style>
  </div>
);

// 2. Star Rating (Icon + Text)
const StarRating = ({ rating }: { rating: number }) => (
  <div className="ir-stars">
    <span className="ir-star-text">{rating} ستاره</span>
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
      <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
    </svg>
    <style jsx>{`
      .ir-stars { display: flex; align-items: center; gap: 4px; color: #6c757d; font-size: 0.8rem; }
      .ir-star-text { margin-top: 2px; } /* تنظیم ریز تراز متن */
      svg { color: #9ca3af; } /* رنگ خاکستری آیکون ستاره */
    `}</style>
  </div>
);

// 3. Image Component
const CardImage = ({ src, alt }: { src: string; alt: string }) => {
  const [error, setError] = useState(false);
  return (
    <div className="ir-img-container">
      <img
        src={error ? '/images/placeholder-image.png' : src}
        alt={alt}
        onError={() => setError(true)}
        className="ir-img"
      />
      <style jsx>{`
      .ir-img-container {
  height: auto; /* به جای 160px */
  padding-top: 56.25%; /* 16:9 */
  position: relative;
}
.ir-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
      `}</style>
    </div>
  );
};

// 4. Single Card Component
const OfferCard = ({ data }: { data: AccommodationData }) => {
  const { name, enName, cityName, starRating, media } = data;
  
  const imageUrl = media && media[0]?.thumbnailURL 
    ? `${imageBase}/${media[0].thumbnailURL}` : '';

  const displayName = name || enName || 'هتل';
  
  // ساخت لینک
  const href = `/accommodation/${createSlug(cityName || '')}/${createSlug(enName || name || '')}`;

  return (
    <Link href={href} className="ir-card-link">
      <div className="ir-card">
        {/* تصویر بالا */}
        <div className="ir-card-top">
          <CardImage src={imageUrl} alt={displayName} />
        </div>

        {/* بدنه کارت */}
        <div className="ir-card-body">
          {/* بج سبز رنگ: متن ثابت طبق درخواست */}
          <div className="ir-badge-container">
             <span className="ir-badge">نزدیک به نمایشگاه بین‌المللی</span>
          </div>

          {/* عنوان هتل */}
          <h3 className="ir-title">{displayName}</h3>

          {/* خط جداکننده */}
          <div className="ir-divider"></div>

          {/* فوتر: ستاره و دکمه */}
          <div className="ir-footer">
            <span className="ir-select-btn">انتخاب کنید</span>
            <StarRating rating={starRating} />
          </div>
        </div>
      </div>

      <style jsx>{`
        /* حذف کامل خط زیر لینک */
        .ir-card-link {
          text-decoration: none !important;
          color: inherit !important;
          display: block;
          height: 100%;
        }
        
        .ir-card {
          background: #fff;
          border-radius: 20px;
          padding: 12px;
          height: 100%;
          display: flex;
          flex-direction: column;
          /* ایزوله کردن استایل باکس */
          box-shadow: none;
          border: none;
          transition: transform 0.2s ease;
        }
        
        .ir-card:hover {
          transform: translateY(-5px);
        }

        .ir-card-top { margin-bottom: 12px; }

        .ir-card-body {
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .ir-badge-container {
            display: flex;
            justify-content: center;
            margin-bottom: 10px;
        }

        /* استایل دقیق تگ سبز */
        .ir-badge {
          background-color: #D8F3B0; /* سبز خیلی روشن */
          color:rgb(0, 0, 0); /* سبز تیره */
          font-size: 0.75rem;
          font-weight: 400;
          padding: 6px 16px;
          border-radius: 6px;
          white-space: nowrap;
        }

        /* عنوان هتل */
        .ir-title {
          font-size: 1rem;
          font-weight: 500;
          color: #333;
          text-align: center;
          margin: 0 0 12px 0;
          line-height: 1.5;
          text-decoration: none !important; /* اطمینان دوچندان */
        }

        .ir-divider {
          width: 100%;
          height: 1px;
          background-color: #eee;
          margin-bottom: 12px;
          margin-top: auto; /* چسباندن محتوای بالا به بالا */
        }

        .ir-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .ir-select-btn {
          color: #003B95; /* آبی */
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
        }
          
      `}</style>
    </Link>
  );
};

// --- Main Section Component ---
const SpecialOffersSection = ({
  initialItems,
}: {
  initialItems?: AccommodationData[];
}) => {
  const [items, setItems] = useState<AccommodationData[]>(initialItems ?? []);
  const [loading, setLoading] = useState(initialItems === undefined);

  useEffect(() => {
    if (initialItems !== undefined) {
      return;
    }
    const fetchData = async () => {
      try {
        const res = await apiClientWithoutAuth.get<ApiResponse>(`${API_ENDPOINT}?pageNumber=1&pageSize=10`);
        if (res.data?.value?.data?.items) {
          setItems(res.data.value.data.items.slice(0, DISPLAY_LIMIT));
        }
      } catch (error) {
        console.error("Error loading offers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [initialItems]);

  if (!loading && items.length === 0) return null;

  return (
    <section className="ir-section">
      <div className="container">
        
        {/* عنوان راست چین */}
        <div className="ir-header-row">
          <h2 className="ir-heading">پیشنهادات ویژه ایرانیجا</h2>
        </div>

        {/* باکس آبی اصلی */}
        <div className="ir-blue-container">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="ir-grid">
              {items.map((item) => (
                <div key={item.id} className="ir-col">
                  <OfferCard data={item} />
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* استایل‌های کلی سکشن - بدون گلوبال برای جلوگیری از تداخل */}
      <style jsx>{`
        .ir-section {
  width: 100%;
  padding: 10px 20px; /* اضافه شد جهت هماهنگی با کامپوننت بالا */
  margin-top: 3rem;
  margin-bottom: 3rem;
  direction: rtl;
}

        /* هدر کاملا راست چین */
        .ir-header-row {
          display: flex;
          justify-content: flex-start; /* راست چین در RTL */
          margin-bottom: 1rem;
          padding-right: 5px;
        }

        .ir-heading {
          color: #4b5563;
          font-size: 1.3rem;
          font-weight: 800;
          margin: 0;
        }
.container {
  max-width: 1250px;
  margin: 0 auto;
}
        /* کانتینر آبی دقیق مثل تصویر */
        .ir-blue-container {
          background-color: #003B95; /* کد رنگ آبی سرمه‌ای */
          border-radius: 24px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 51, 128, 0.15);
        }

        /* گرید ۳ ستونه */
        .ir-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .ir-col {
          min-width: 0;
        }

        /* ریسپانسیو */
        @media (max-width: 992px) {
          .ir-grid { grid-template-columns: repeat(2, 1fr); }
          .ir-col:nth-child(3) { display: none; } /* در تبلت دوتایی شود */
        }

        @media (max-width: 576px) {
          .ir-grid { grid-template-columns: 1fr; }
          .ir-col:nth-child(3) { display: block; }
          .ir-blue-container { padding: 15px; border-radius: 16px; }
          .ir-heading { font-size: 1.1rem; }
        }
      `}</style>
    </section>
  );
};

export default SpecialOffersSection;